import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Users, ArrowLeft, RefreshCw, Plus, Phone, Mail, Building, MessageCircle,
  ChevronRight, Search, Filter, UserPlus, TrendingUp, Target, Download
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STAGES = [
  { key: 'new', label: 'New', color: 'bg-blue-500' },
  { key: 'contacted', label: 'Contacted', color: 'bg-yellow-500' },
  { key: 'qualified', label: 'Qualified', color: 'bg-purple-500' },
  { key: 'proposal_sent', label: 'Proposal', color: 'bg-orange-500' },
  { key: 'won', label: 'Won', color: 'bg-green-500' },
  { key: 'onboarded', label: 'Onboarded', color: 'bg-emerald-600' },
  { key: 'lost', label: 'Lost', color: 'bg-red-500' }
];

const LeadPipeline = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  const availableStages = useMemo(
    () => STAGES.filter(s => s.key !== selectedLead?.status),
    [selectedLead]
  );
  const [newNote, setNewNote] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [newLead, setNewLead] = useState({ name: '', email: '', phone: '', company: '', source: 'manual', notes: '' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, pipelineRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/leads?${statusFilter ? `status=${statusFilter}` : ''}`),
        fetch(`${BACKEND_URL}/api/admin/leads/pipeline`)
      ]);
      if (leadsRes.ok) setLeads((await leadsRes.json()).leads || []);
      if (pipelineRes.ok) setPipeline(await pipelineRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const syncFromTracking = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/leads/sync-from-tracking`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        alert(`Imported ${data.imported} new leads from website tracking`);
        fetchData();
      }
    } catch (e) { console.error(e); }
    finally { setSyncing(false); }
  };

  const createLead = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/leads`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newLead)
      });
      if (res.ok) {
        setShowCreate(false);
        setNewLead({ name: '', email: '', phone: '', company: '', source: 'manual', notes: '' });
        fetchData();
      }
    } catch (e) { console.error(e); }
  };

  const updateStatus = async (leadId, newStatus) => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/leads/${leadId}/status`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      fetchData();
      if (selectedLead?.lead_id === leadId) {
        setSelectedLead(prev => ({ ...prev, status: newStatus }));
      }
    } catch (e) { console.error(e); }
  };

  const addNote = async (leadId) => {
    if (!newNote.trim()) return;
    try {
      await fetch(`${BACKEND_URL}/api/admin/leads/${leadId}/note`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: newNote })
      });
      setNewNote('');
      // Refresh lead detail
      const res = await fetch(`${BACKEND_URL}/api/admin/leads/${leadId}`);
      if (res.ok) setSelectedLead(await res.json());
    } catch (e) { console.error(e); }
  };

  const filteredLeads = leads.filter(l =>
    !filter || (l.name || '').toLowerCase().includes(filter.toLowerCase()) ||
    (l.email || '').toLowerCase().includes(filter.toLowerCase()) ||
    (l.phone || '').includes(filter)
  );

  const getStageColor = (status) => STAGES.find(s => s.key === status)?.color || 'bg-gray-400';

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => navigate('/admin/dashboard')} size="sm">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900" data-testid="leads-title">Lead Pipeline</h1>
              <p className="text-sm text-gray-600">Track & convert leads into clients</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={syncFromTracking} variant="outline" size="sm" disabled={syncing} data-testid="sync-leads-btn">
              <Download className={`w-4 h-4 mr-1 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Website Leads'}
            </Button>
            <Button onClick={() => setShowCreate(true)} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-lead-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Lead
            </Button>
            <Button onClick={fetchData} variant="outline" size="sm" disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Pipeline Stats */}
        {pipeline && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <Card><CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg"><Users className="w-5 h-5 text-blue-600" /></div>
              <div><p className="text-2xl font-bold">{pipeline.total}</p><p className="text-xs text-gray-500">Total Leads</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg"><TrendingUp className="w-5 h-5 text-green-600" /></div>
              <div><p className="text-2xl font-bold">{pipeline.conversion_rate}%</p><p className="text-xs text-gray-500">Win Rate</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg"><Target className="w-5 h-5 text-purple-600" /></div>
              <div><p className="text-2xl font-bold">{(pipeline.stages?.qualified || 0) + (pipeline.stages?.proposal_sent || 0)}</p><p className="text-xs text-gray-500">In Pipeline</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg"><UserPlus className="w-5 h-5 text-emerald-600" /></div>
              <div><p className="text-2xl font-bold">{pipeline.stages?.onboarded || 0}</p><p className="text-xs text-gray-500">Onboarded</p></div>
            </CardContent></Card>
          </div>
        )}

        {/* Stage Filter Chips */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          <button onClick={() => setStatusFilter('')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${!statusFilter ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            All ({pipeline?.total || 0})
          </button>
          {STAGES.map(s => (
            <button key={s.key} onClick={() => setStatusFilter(s.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${statusFilter === s.key ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {s.label} ({pipeline?.stages?.[s.key] || 0})
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Search by name, email, or phone..." className="pl-10" data-testid="lead-search" />
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          {/* Leads List */}
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>
                ) : filteredLeads.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Users className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                    <p>No leads found</p>
                    <p className="text-sm mt-1">Sync from website tracking or add manually</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {filteredLeads.map((lead) => (
                      <div key={lead.lead_id} onClick={() => setSelectedLead(lead)}
                        data-testid={`lead-row-${lead.lead_id}`}
                        className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${selectedLead?.lead_id === lead.lead_id ? 'bg-emerald-50 border-l-4 border-emerald-500' : ''}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-semibold text-gray-900 truncate">{lead.name || 'Unnamed'}</p>
                              <span className={`inline-block w-2 h-2 rounded-full ${getStageColor(lead.status)}`}></span>
                              <span className="text-xs text-gray-500 capitalize">{lead.status?.replace('_', ' ')}</span>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-gray-500">
                              {lead.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{lead.email}</span>}
                              {lead.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone}</span>}
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Lead Detail / Create Form */}
          <div>
            {showCreate ? (
              <Card>
                <CardHeader><CardTitle className="text-lg">Add New Lead</CardTitle></CardHeader>
                <CardContent>
                  <form onSubmit={createLead} className="space-y-3">
                    <Input placeholder="Name *" required value={newLead.name} onChange={e => setNewLead({...newLead, name: e.target.value})} data-testid="new-lead-name" />
                    <Input placeholder="Email" type="email" value={newLead.email} onChange={e => setNewLead({...newLead, email: e.target.value})} data-testid="new-lead-email" />
                    <Input placeholder="Phone" value={newLead.phone} onChange={e => setNewLead({...newLead, phone: e.target.value})} />
                    <Input placeholder="Company" value={newLead.company} onChange={e => setNewLead({...newLead, company: e.target.value})} />
                    <select value={newLead.source} onChange={e => setNewLead({...newLead, source: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm">
                      <option value="manual">Manual</option>
                      <option value="website">Website</option>
                      <option value="referral">Referral</option>
                      <option value="ad">Ad Campaign</option>
                    </select>
                    <Textarea placeholder="Notes" value={newLead.notes} onChange={e => setNewLead({...newLead, notes: e.target.value})} rows={2} />
                    <div className="flex gap-2">
                      <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-lead-btn">Save Lead</Button>
                      <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            ) : selectedLead ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <span className={`w-3 h-3 rounded-full ${getStageColor(selectedLead.status)}`}></span>
                    {selectedLead.name || 'Unnamed'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Contact Info */}
                  <div className="space-y-2 text-sm">
                    {selectedLead.email && <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-gray-400" />{selectedLead.email}</p>}
                    {selectedLead.phone && <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-gray-400" />{selectedLead.phone}</p>}
                    {selectedLead.company && <p className="flex items-center gap-2"><Building className="w-4 h-4 text-gray-400" />{selectedLead.company}</p>}
                    <p className="text-xs text-gray-500">Source: {selectedLead.source}</p>
                  </div>

                  {/* Status Actions */}
                  <div>
                    <p className="text-xs font-medium text-gray-600 mb-2">Move to:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {availableStages.map(s => (
                        <button key={s.key} onClick={() => updateStatus(selectedLead.lead_id, s.key)}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium text-white ${s.color} hover:opacity-80 transition-opacity`}
                          data-testid={`status-btn-${s.key}`}>
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex gap-2">
                    {selectedLead.phone && (
                      <a href={`https://wa.me/${(selectedLead.phone || '').replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer"
                        className="flex-1 text-center bg-green-500 text-white py-2 rounded-lg text-sm font-medium hover:bg-green-600">
                        WhatsApp
                      </a>
                    )}
                    {selectedLead.email && (
                      <a href={`mailto:${selectedLead.email}`}
                        className="flex-1 text-center bg-blue-500 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-600">
                        Email
                      </a>
                    )}
                  </div>

                  {/* Notes */}
                  <div>
                    <p className="text-xs font-medium text-gray-600 mb-2">Notes</p>
                    <div className="space-y-2 max-h-40 overflow-y-auto mb-2">
                      {(selectedLead.notes || []).map((n, i) => (
                        <div key={note.text?.slice(0,20) || `note-${i}`} className="bg-gray-50 rounded-lg p-2 text-xs">
                          <p className="text-gray-800">{n.text}</p>
                          <p className="text-gray-400 mt-1">{n.timestamp ? new Date(n.timestamp).toLocaleString('en-IN') : ''}</p>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Add a note..." className="text-sm" data-testid="add-note-input" />
                      <Button size="sm" onClick={() => addNote(selectedLead.lead_id)} className="bg-gray-800 text-white" data-testid="add-note-btn">Add</Button>
                    </div>
                  </div>

                  {/* Activity Timeline */}
                  <div>
                    <p className="text-xs font-medium text-gray-600 mb-2">Activity</p>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {(selectedLead.activities || []).slice().reverse().map((a, i) => (
                        <div key={`activity-${i}`} className="flex items-start gap-2 text-xs">
                          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0"></div>
                          <div>
                            <p className="text-gray-700">{a.description}</p>
                            <p className="text-gray-400">{a.timestamp ? new Date(a.timestamp).toLocaleString('en-IN') : ''}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-8 text-center text-gray-500">
                  <Users className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                  <p>Select a lead to view details</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadPipeline;
